import { forwardRef } from 'react';

import { Plus } from '../../icons/lucide';
import { Pressable } from '../../primitives/Pressable';
import { Text } from '../../primitives/Text';
import { roleColor } from '../../styles/brand-colors';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import type { WidgetRailEndCellProps } from './WidgetRailEndCell.types';

/** The rail cards' outer radius (`WidgetCard`), so the cell reads as one more
 *  slot in the row. */
const CELL_CLASS =
  'flex flex-col items-center justify-center rounded-[36px] border border-dashed border-secondary-300 bg-secondary-50/50 active:bg-secondary-100 web:hover:bg-secondary-100';

/**
 * The tap area that ENDS the Home widget rail — a dashed slot the size of
 * a card with a "+" and a caption, after the last widget: the place a
 * student customises their widgets from, where the rail runs out. One look on
 * both clients (the phone and iPad rail, web's Home rail); what it opens is
 * the caller's (the app's Add-widgets sheet, web's Customise popover).
 *
 * `forwardRef`, and every extra prop reaches the pressable, so web can hand it
 * to a Radix `PopoverTrigger asChild` as is.
 */
export const WidgetRailEndCell = forwardRef<unknown, WidgetRailEndCellProps>(
  function WidgetRailEndCell({ label, onPress, className, style, testID, ...rest }, ref) {
    const { isDark } = useTheme();
    return (
      <Pressable
        {...rest}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- shared/RN ref shapes differ per platform
        ref={ref as any}
        // Arrow, not the bare callback: a press event must never arrive as an
        // argument the caller reads as something else.
        onPress={onPress ? () => onPress() : undefined}
        accessibilityLabel={label}
        accessibilityRole="button"
        testID={testID}
        className={cn(CELL_CLASS, className)}
        webProps={{ type: 'button' }}
        nativeProps={style ? { style } : undefined}>
        <Plus size={24} color={roleColor('fg-subtle', isDark)} />
        <Text className="mt-2 px-3 text-center text-b3 font-medium text-fg-subtle native:font-mona-medium">
          {label}
        </Text>
      </Pressable>
    );
  },
);
